import { TFunction } from 'i18next'
import { capacityPaths } from 'capacity/paths'
import { SectionCapacityRow } from './capacityRows'
import { GroupBlock } from './groupBlocks'
import { ResolvedBlocks } from '../useGroupBlocks'

// What one part of the Capacity block shows. Deciding is done here, drawing in SectionPart.
export type PartState =
    | { kind: 'pending' }
    | { kind: 'message'; text: string }
    | { kind: 'lines'; lines: string[]; linkTo: string; linkLabel: string }
    | { kind: 'rows'; rows: SectionCapacityRow[] }
    | { kind: 'blocks'; blocks: GroupBlock[]; lead?: string }

const pending: PartState = { kind: 'pending' }
const message = (text: string): PartState => ({ kind: 'message', text })
const goToTimeSlots = (lines: string[], accountSlug: string, t: TFunction): PartState => ({
    kind: 'lines',
    lines,
    linkTo: capacityPaths.timeSlots(accountSlug),
    linkLabel: t('capacity.formSection.goToTimeSlots'),
})

interface CapacityPartInput {
    isNew: boolean
    loading: boolean
    failed: boolean
    rows: SectionCapacityRow[]
}

const capacityPart = ({ loading, failed, rows }: CapacityPartInput, emptyText: string, t: TFunction): PartState => {
    if (loading) {
        return pending
    }
    if (failed) {
        return message(t('capacity.formSection.capacitiesFailed'))
    }
    return rows.length === 0 ? message(emptyText) : { kind: 'rows', rows }
}

export const productCapacityPart = (input: CapacityPartInput, t: TFunction): PartState =>
    input.isNew
        ? message(t('capacity.formSection.saveProductFirst'))
        : capacityPart(input, t('capacity.formSection.notInAny'), t)

export const categoryCapacityPart = (input: CapacityPartInput, t: TFunction): PartState =>
    input.isNew
        ? message(t('capacity.formSection.saveCategoryFirst'))
        : capacityPart(input, t('capacity.formSection.categoryNone'), t)

interface ProductTimeSlotsInput {
    accountSlug: string
    isNew: boolean
    isBundle: boolean
    failed: boolean
    resolved: ResolvedBlocks | null
    // Every category of the product has time slots switched off: the reason no group applies.
    switchedOff: boolean
}

export function productTimeSlotsPart(input: ProductTimeSlotsInput, t: TFunction): PartState {
    const { accountSlug, isNew, isBundle, failed, resolved, switchedOff } = input
    if (isNew) {
        return message(t('capacity.formSection.saveProductFirstTimeSlots'))
    }
    if (isBundle) {
        return message(t('capacity.formSection.bundleTimeSlots'))
    }
    if (failed) {
        return message(t('capacity.formSection.groupsFailed'))
    }
    if (!resolved) {
        return pending
    }
    if (resolved.blocks.length === 0) {
        const why = switchedOff ? t('capacity.formSection.switchedOff') : t('capacity.formSection.noGroupProduct')
        return goToTimeSlots([t('capacity.formSection.noTimeSlots'), why], accountSlug, t)
    }
    return { kind: 'blocks', blocks: resolved.blocks, lead: resolved.lead }
}

interface CategoryTimeSlotsInput {
    accountSlug: string
    isNew: boolean
    enabled: boolean
    failed: boolean
    resolved: ResolvedBlocks | null
}

export function categoryTimeSlotsPart(input: CategoryTimeSlotsInput, t: TFunction): PartState {
    const { accountSlug, isNew, enabled, failed, resolved } = input
    if (isNew) {
        return message(t('capacity.formSection.saveCategoryFirstTimeSlots'))
    }
    if (!enabled) {
        return message(t('capacity.formSection.offLine'))
    }
    if (failed) {
        return message(t('capacity.formSection.groupsFailed'))
    }
    if (!resolved) {
        return pending
    }
    if (resolved.blocks.length === 0) {
        return goToTimeSlots([t('capacity.formSection.noGroupCategory')], accountSlug, t)
    }
    return { kind: 'blocks', blocks: resolved.blocks }
}
